import { ProductType } from '../../../__generated__/graphql';

/**
 * What a brand sells, as the platform owner picks it on /admin/brands (spec 016 O4).
 * The backend keeps two fields: `defaultProductType` and `allowedProductTypes`
 * (empty = every type except `house`, the rule from before spec 016). The page
 * offers the two choices a brand actually has today.
 */
export type BrandProduct = 'cars' | 'stays';

export interface BrandProductTypesLike {
  defaultProductType?: ProductType | string | null;
  allowedProductTypes?: (ProductType | string)[] | null;
}

const HOUSE = ProductType.House;

/** A brand sells places to stay when it lists `house` or defaults to it. */
export function brandProductOf(brand: BrandProductTypesLike): BrandProduct {
  const allowed = (brand.allowedProductTypes ?? []).map(t => String(t).toUpperCase());
  const fallback = String(brand.defaultProductType ?? '').toUpperCase();
  return allowed.includes(HOUSE) || fallback === HOUSE ? 'stays' : 'cars';
}

/** The `updateBrandProductTypes` / `createBrand` fields for a choice. */
export function productTypesInput(product: BrandProduct): {
  defaultProductType: ProductType;
  allowedProductTypes: ProductType[];
} {
  return product === 'stays'
    ? { defaultProductType: ProductType.House, allowedProductTypes: [ProductType.House] }
    : { defaultProductType: ProductType.Car, allowedProductTypes: [] };
}

export const BRAND_PRODUCT_LABELS: Record<BrandProduct, string> = {
  cars: 'Cars',
  stays: 'Places to stay',
};

/** A fee percent typed by the admin: 0 to 30 (the API's limit) with up to two decimals, else null. */
export function parseFeePercent(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= 0 && n <= 30 ? n : null;
}
